import {CreateDataArgs, ImageOpHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal"
import {
    AnyImageOpHalData,
    CtorArgs,
    ImageOpHalData,
    MapToDataType,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-data"
import {ImageOpHalParameters, UpdateImageOptions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-types"
import {
    colorFromRemotelyColorLike,
    indexGlslVectorType,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-utils"
import {RegionLockedHalImageRef} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/region-locked-hal-image-ref"
import {SourceType} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/image-op-per-pixel-fn/definition"
import {ImageOpImgProc} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/imgproc/image-op-imgproc"
import {ImageOpDefinition, EvaluatedImageOpValue} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {ImageOpContextHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-hal"
import {Descriptor, MipLevelRegion, Regions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"
import {getBatching} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/utils/image-op-utils"
import {HalImage, HalPainterParametersType} from "@cm/hal"
import {HalPainterImageCompositorRef} from "@cm/hal/hal-ref"
import {Vector2, Vector2Like} from "@cm/math"
import {deepCopy} from "@cm/utils"
import {MAX_TEXTURE_UNITS} from "@cm/webgl2"
import {UPDATE_COMPLEXITY_MID} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/update-complexity"

const ALLOW_MERGING = true

export type AnyImageOpPerPixelFnHalData = Data<ImageOpDefinition>

export class ImageOpPerPixelFnHal<Definition extends ImageOpDefinition> extends ImageOpHal<Definition, Data<Definition>> {
    constructor(
        ctx: ImageOpContextHal,
        parameters: ImageOpHalParameters<Definition>,
        private outputDescriptor: Descriptor,
        readonly compositingInfo: HalCompositorInfo,
    ) {
        super(ctx)
        this.setParameters(parameters)
    }

    setParametersAndOutputDescriptor(parameters: ImageOpHalParameters<Definition>, outputDescriptor: Descriptor) {
        this.outputDescriptor = outputDescriptor
        this.setParameters(parameters)
    }

    protected override createData(args: CreateDataArgs<Definition, Data<Definition>>): Data<Definition> {
        const sourceValues = this.compositingInfo.sourceParameterNames.map(
            (sourceParameterName) => args.parameters[sourceParameterName] as EvaluatedImageOpValue<MapToDataType<SourceType>>,
        )
        return new Data({
            ...args,
            descriptor: deepCopy(this.outputDescriptor),
            outputImageReuse: "reuse-if-same-descriptor",
            updateComplexity: UPDATE_COMPLEXITY_MID,
            sourceValues,
            compositingInfo: deepCopy(this.compositingInfo),
        })
    }
}

export class Data<Definition extends ImageOpDefinition> extends ImageOpHalData<Definition> {
    constructor(
        args: CtorArgs<Definition, Data<Definition>> & {
            sourceValues: EvaluatedImageOpValue<MapToDataType<SourceType>>[]
            compositingInfo: HalCompositorInfo
        },
    ) {
        super(args)
        this.compositingInfo = args.compositingInfo
        this.sourceValues = args.sourceValues
    }

    protected override dispose() {
        this.releaseSourceInfo()
        this.releaseCompositor()
        super.dispose()
    }

    protected async writeRegion(outputImage: HalImage, mipLevelRegion: MipLevelRegion, options?: UpdateImageOptions): Promise<void> {
        // if (this._isForwardingTo) {
        //     throw new Error("Attempting to write to a forwarding per-pixel-fn")
        // }
        const {sourceImages} = this.sourceInfo
        sourceImages.forEach((sourceImage) => sourceImage.addRef("ImageOpPerPixelFn.writeRegion - sourceImage"))
        const sourceHalImages: (RegionLockedHalImageRef | undefined)[] = []
        try {
            const outputBatching = getBatching(this.descriptor)
            const sourceParameters = this.getCurrentSourceParameters()
            // update source images
            const sourceImageNumPatches: Vector2Like[] = []
            for (const sourceImage of sourceImages) {
                // check for broadcasting source
                const batching = getBatching(sourceImage.descriptor)
                let halImage: RegionLockedHalImageRef | undefined
                if (batching.patchSize.width === 1 && batching.patchSize.height === 1) {
                    halImage = await sourceImage.getUpdatedHalImage(
                        {
                            x: 0,
                            y: 0,
                            width: batching.numPatches.x,
                            height: batching.numPatches.y,
                            mipLevel: 0,
                        },
                        options,
                    )
                } else {
                    halImage = await sourceImage.getUpdatedHalImage(mipLevelRegion, options)
                }
                sourceHalImages.push(halImage)
                sourceImageNumPatches.push(batching.numPatches)
            }
            this.compositor.ref.paint({
                target: outputImage,
                sourceImages: sourceHalImages.map((sourceHalImage) => sourceHalImage?.halImage),
                parameters: {
                    ...sourceParameters,
                    u_sourceImageNumPatches: {type: "int2[]", value: sourceImageNumPatches},
                    u_outputPatchSize: {type: "int2", value: Vector2.fromSize2Like(outputBatching.patchSize)},
                    u_targetMipLevel: {type: "int", value: mipLevelRegion.mipLevel},
                },
                options: {
                    targetRegion: mipLevelRegion,
                    targetMipLevel: mipLevelRegion.mipLevel,
                },
            })
        } finally {
            sourceHalImages.forEach((sourceHalImage) => sourceHalImage?.release())
            sourceImages.forEach((sourceImage) => sourceImage.release("ImageOpPerPixelFn.writeRegion - sourceImage"))
        }
    }

    protected override onSourceRegionsInvalidated(sourceImage: AnyImageOpHalData, sourceRegions: Regions) {
        if (sourceImage.descriptor.width === 1 || sourceImage.descriptor.height === 1) {
            // if the source image is a broadcast source, we need to invalidate all pages
            this.invalidateRegions()
        } else {
            // otherwise, we just forward the invalidation
            this.invalidateRegions(sourceRegions)
        }
    }

    private get sourceInfo(): SourceInfo {
        if (!this._sourceInfo) {
            this._sourceInfo = this.gatherSourceInfo(this.sourceValues)
        }
        return this._sourceInfo
    }

    private get compositor(): HalPainterImageCompositorRef {
        if (!this._compositor) {
            this._compositor = this.createMergedCompositor(this.sourceInfo)
        }
        return this._compositor
    }

    private gatherSourceInfo(sourceValues: EvaluatedImageOpValue<MapToDataType<SourceType>>[]): SourceInfo {
        const pixelTransformSourcesToProcess: [AnyImageOpPerPixelFnHalData, number][] = []
        sourceValues.forEach((_sourceValue, index) => pixelTransformSourcesToProcess.push([this, index]))
        const sourceImageSet = new Set<AnyImageOpHalData>()
        const forwardedPerPixelFnSet = new Set<AnyImageOpPerPixelFnHalData>()
        const sourceParameterInfos: SourceParameterInfo[] = []
        const processedSourceIndicesByPixelTransform = new Map<AnyImageOpPerPixelFnHalData, Set<number>>()
        const pixelTransformSourcesToProcessLater: [AnyImageOpPerPixelFnHalData, number][] = []
        do {
            pixelTransformSourcesToProcessLater.forEach((source) => pixelTransformSourcesToProcess.push(source))
            pixelTransformSourcesToProcessLater.length = 0
            const lastNumForwardedPerPixelFns = forwardedPerPixelFnSet.size
            for (;;) {
                const pixelTransformSource = pixelTransformSourcesToProcess.pop()
                if (!pixelTransformSource) {
                    break
                }
                const pixelTransform = pixelTransformSource[0]
                const index = pixelTransformSource[1]
                let processedSourceIndices = processedSourceIndicesByPixelTransform.get(pixelTransform)
                if (!processedSourceIndices) {
                    processedSourceIndices = new Set<number>()
                    processedSourceIndicesByPixelTransform.set(pixelTransform, processedSourceIndices)
                }
                const sourceValue = pixelTransform.sourceValues[index]
                let processAgain = false
                if (sourceValue instanceof Data) {
                    // if (sourceValue.isForwardingTo && sourceValue.isForwardingToEventually !== this) {
                    //     // this pixel transform is already compiled; if it is part of this cluster, we can skip it, otherwise we need to consider this a external source
                    //     if (!forwardedPerPixelFnSet.has(sourceValue)) {
                    //         sourceImageSet.add(sourceValue)
                    //     }
                    // } else {
                    if (!forwardedPerPixelFnSet.has(sourceValue)) {
                        // this is a potential merging candidate
                        // make sure all consumers are part of this merge
                        let allConsumersValid = ALLOW_MERGING
                        for (const consumer of sourceValue.consumers) {
                            if (!allConsumersValid) {
                                break
                            }
                            if (!(consumer instanceof Data)) {
                                // source has consumers which are not per pixel functions; can not merge this
                                allConsumersValid = false
                            } else if (!forwardedPerPixelFnSet.has(consumer) && consumer !== this) {
                                // source has consumers which are per pixel functions which are not (yet) merged into this cluster; attempt again later
                                pixelTransformSourcesToProcessLater.push([pixelTransform, index])
                                allConsumersValid = false
                                processAgain = true
                            }
                        }
                        if (allConsumersValid) {
                            // let's make sure we don't exceed the max texture units when merging this
                            const totalSourceImages = new Set([...sourceValue.sourceImages, ...sourceImageSet]).size
                            if (totalSourceImages < MAX_TEXTURE_UNITS) {
                                // let's merge this source
                                // if (sourceValue._isForwardingTo) {
                                //     console.warn("ImageOpMergedPixelTransformHal: Source pixel transform is already forwarding; this should not happen")
                                // }
                                // sourceValue._isForwardingTo = this
                                sourceImageSet.delete(sourceValue)
                                forwardedPerPixelFnSet.add(sourceValue)
                                sourceValue.sourceValues.forEach((_, index) => pixelTransformSourcesToProcess.push([sourceValue, index]))
                            } else {
                                sourceImageSet.add(sourceValue)
                            }
                        } else {
                            sourceImageSet.add(sourceValue)
                        }
                    }
                } else if (sourceValue instanceof ImageOpHalData) {
                    sourceImageSet.add(sourceValue)
                } else if (sourceValue instanceof ImageOpImgProc) {
                    throw new Error("ImageOpMergedPixelTransformHal: ImageOpImgProc not expected here")
                } else {
                    sourceParameterInfos.push({
                        parameterName: `param${sourceParameterInfos.length}`,
                        source: pixelTransform,
                        sourceIndex: index,
                    })
                }
                if (!processAgain) {
                    processedSourceIndices.add(index)
                }
            }
            if (lastNumForwardedPerPixelFns === forwardedPerPixelFnSet.size) {
                // no new pixel transforms were added; we are done
                break
            }
        } while (pixelTransformSourcesToProcessLater.length > 0)
        const sourceImages = Array.from(sourceImageSet)
        const forwardedPerPixelFns = Array.from(forwardedPerPixelFnSet)
        sourceImages.forEach((sourceImage) => sourceImage.addRef("ImageOpPerPixelFn.sourceImage"))
        forwardedPerPixelFns.forEach((forwardedPerPixelFn) => forwardedPerPixelFn.addRef("ImageOpPerPixelFn.forwardedPerPixelFn"))
        sourceParameterInfos
            .filter((parameterInfo) => parameterInfo.source !== this)
            .forEach((parameterInfo) => parameterInfo.source.addRef("ImageOpPerPixelFn.sourceParameterInfo"))
        return {
            sourceImages,
            forwardedPerPixelFns,
            sourceParameterInfos,
        }
    }

    private releaseSourceInfo() {
        if (!this._sourceInfo) {
            return
        }
        this._sourceInfo.sourceImages.forEach((sourceImage) => sourceImage.release("ImageOpPerPixelFn.sourceImage"))
        this._sourceInfo.forwardedPerPixelFns.forEach((forwardedPerPixelFn) => forwardedPerPixelFn.release("ImageOpPerPixelFn.forwardedPerPixelFn"))
        this._sourceInfo.sourceParameterInfos
            .filter((parameterInfo) => parameterInfo.source !== this)
            .forEach((parameterInfo) => parameterInfo.source.release("ImageOpPerPixelFn.sourceParameterInfo"))
        this._sourceInfo = null
    }

    private releaseCompositor() {
        this._compositor?.release()
        this._compositor = null
    }

    private createMergedCompositor(sourceInfo: SourceInfo): HalPainterImageCompositorRef {
        let parameterDeclarations = ""
        sourceInfo.sourceParameterInfos.forEach((info) => {
            parameterDeclarations += `          uniform vec4 ${info.parameterName};\n`
        })
        parameterDeclarations += `
                uniform ivec2 u_outputPatchSize;
                uniform ivec2 u_sourceImageNumPatches[${MAX_TEXTURE_UNITS}];
                
                ivec2 wrapBatchedTexelIndex(ivec2 patchTexelIndex, ivec2 batchIndex, ivec2 patchSize, ivec2 batchSize) {
                    batchIndex.x = wrapInt(batchIndex.x, batchSize.x);
                    batchIndex.y = wrapInt(batchIndex.y, batchSize.y);
                    patchTexelIndex.x = wrapInt(patchTexelIndex.x, patchSize.x);
                    patchTexelIndex.y = wrapInt(patchTexelIndex.y, patchSize.y);
                    return batchIndex * patchSize + patchTexelIndex;
                }
                  
                ivec2 computeBatchedTexelIndex(int index, ivec2 targetPixel) {
                    ivec2 batchSize = u_sourceImageNumPatches[index];
                    ivec2 patchSize = ivec2(u_viewSize[index]) / batchSize;
                    ivec2 batchIndex = targetPixel / u_outputPatchSize;
                    ivec2 patchTexelIndex = targetPixel % u_outputPatchSize;
                    return wrapBatchedTexelIndex(patchTexelIndex, batchIndex, patchSize, batchSize);
                } 
        `

        const outputBatching = getBatching(this.descriptor)
        const individualCompositingFunctions: string[] = []
        let compositingFunction = ""
        const valueNameByImageOp = new Map<AnyImageOpHalData, string>()
        sourceInfo.sourceImages.forEach((sourceImage, index) => {
            const inputName = `input${index}`
            valueNameByImageOp.set(sourceImage, inputName)
            const batching = getBatching(sourceImage.descriptor)
            if (batching.numPatches.x !== 1 && batching.numPatches.x !== outputBatching.numPatches.x) {
                throw new Error("ImageOpFnPixelTransform: Source image numPatches X mismatch")
            }
            if (batching.numPatches.y !== 1 && batching.numPatches.y !== outputBatching.numPatches.y) {
                throw new Error("ImageOpFnPixelTransform: Source image numPatches Y mismatch")
            }
            if (batching.patchSize.width !== 1 && batching.patchSize.width !== outputBatching.patchSize.width) {
                throw new Error("ImageOpFnPixelTransform: Source image width mismatch")
            }
            if (batching.patchSize.height !== 1 && batching.patchSize.height !== outputBatching.patchSize.height) {
                throw new Error("ImageOpFnPixelTransform: Source image height mismatch")
            }
            compositingFunction += `vec4 ${inputName} = texelFetchLod${index}(computeBatchedTexelIndex(${index}, texelIndex), lod, ADDRESS_MODE_REPEAT, vec4(0, 0, 0, 0));\n`
        })

        const processNode = (imageOpPixelTransformHal: AnyImageOpPerPixelFnHalData) => {
            const thisCompositingFnIndex = individualCompositingFunctions.length
            const thisCompositingFnName = `compositingFn${thisCompositingFnIndex}`
            const {compositingFn: thisCompositingFn, compositingFnImageOpArgs: thisCompositingFnImageOpArgs} = this.getCompositingFunction(
                imageOpPixelTransformHal,
                thisCompositingFnName,
            )
            const mappedCompositingFnImageOpArgs = thisCompositingFnImageOpArgs.map((arg) => {
                const valueName = valueNameByImageOp.get(arg.imageOpHalData)
                if (valueName === undefined) {
                    throw new Error("ImageOpFnPixelTransform: Value name not found for source")
                }
                return `${valueName}${indexGlslVectorType(4, arg.channelIndex)}`
            })
            individualCompositingFunctions.push(thisCompositingFn)
            const fnValueName = `fnValue${thisCompositingFnIndex}`
            compositingFunction += `                vec4 ${fnValueName} = ${thisCompositingFnName}(${["texelIndex", "lod", ...mappedCompositingFnImageOpArgs].join(", ")});\n`
            valueNameByImageOp.set(imageOpPixelTransformHal, fnValueName)
        }

        const checkedPixelTransforms = new Set<AnyImageOpPerPixelFnHalData>()
        const traverse = (imageOpPixelTransformHal: AnyImageOpPerPixelFnHalData) => {
            if (checkedPixelTransforms.has(imageOpPixelTransformHal)) {
                return
            }
            checkedPixelTransforms.add(imageOpPixelTransformHal)
            imageOpPixelTransformHal.sourceValues.forEach((sourceValue) => {
                if (sourceValue instanceof Data && sourceInfo.forwardedPerPixelFns.findIndex((perPixelFn) => perPixelFn === sourceValue) >= 0) {
                    // this source image will be merged into the current pixel transform
                    traverse(sourceValue)
                }
            })
            processNode(imageOpPixelTransformHal)
        }
        traverse(this)

        const resultValueName = valueNameByImageOp.get(this)
        if (resultValueName === undefined) {
            throw new Error("ImageOpFnPixelTransform: Result value name not found")
        }
        compositingFunction += `                return ${resultValueName};\n`

        const fullCompositingFunction = `
                ${parameterDeclarations}
                
                ${individualCompositingFunctions.join("\n")}
                
                uniform int u_targetMipLevel;

                vec4 computeColor(ivec2 texelIndex) {
                    int lod = u_targetMipLevel;
                    ${compositingFunction}
                }

            `
        if (Data.logger) {
            Data.logger.trace(`Merged pixel transform function (${this.debugId}):`)
            Data.logger.trace(fullCompositingFunction)
        }
        return this.ctx.createPainter("compositor", fullCompositingFunction)
    }

    private getCompositingFunction(
        imageOpPixelTransformHal: AnyImageOpPerPixelFnHalData,
        compositingFnName: string,
    ): {compositingFn: string; compositingFnImageOpArgs: {imageOpHalData: AnyImageOpHalData; channelIndex: number}[]} {
        const compositingFnArgs: string[] = []
        const compositingFnImageOpArgs: {imageOpHalData: AnyImageOpHalData; channelIndex: number; valueName: string}[] = []
        let compositingFn = imageOpPixelTransformHal.compositingInfo.compositingFunction
        compositingFn = compositingFn.replace(/\$(\d+)/g, "$1") // replace $0, $1, $2, ... with 0, 1, 2, ...
        const regex = /fetchSource(?:Channel)?\((\d+)(?:,\s*(\d+))?\)/g
        let match
        while ((match = regex.exec(compositingFn))) {
            let replacement: string
            if (match[0].startsWith("fetchSourceChannel")) {
                const sourceIndex = Number.parseInt(match[1])
                if (Number.isNaN(sourceIndex) || sourceIndex < 0 || sourceIndex >= imageOpPixelTransformHal.sourceValues.length) {
                    throw new Error("ImageOpFnPixelTransform: Invalid source index: " + sourceIndex)
                }
                const channelIndex = Number.parseInt(match[2])
                if (Number.isNaN(channelIndex) || channelIndex < 0) {
                    throw new Error("ImageOpFnPixelTransform: Invalid channel index: " + channelIndex)
                }
                replacement = this.getSourceChannel(imageOpPixelTransformHal, sourceIndex, channelIndex, compositingFnArgs, compositingFnImageOpArgs)
            } else if (match[0].startsWith("fetchSource")) {
                const sourceIndex = Number.parseInt(match[1])
                const sourceR = this.getSourceChannel(imageOpPixelTransformHal, sourceIndex, 0, compositingFnArgs, compositingFnImageOpArgs)
                const sourceG = this.getSourceChannel(imageOpPixelTransformHal, sourceIndex, 1, compositingFnArgs, compositingFnImageOpArgs)
                const sourceB = this.getSourceChannel(imageOpPixelTransformHal, sourceIndex, 2, compositingFnArgs, compositingFnImageOpArgs)
                const sourceA = this.getSourceChannel(imageOpPixelTransformHal, sourceIndex, 3, compositingFnArgs, compositingFnImageOpArgs)
                replacement = `vec4(${sourceR}, ${sourceG}, ${sourceB}, ${sourceA})`
            } else {
                throw new Error("Unrecognized match: " + match[0])
            }
            compositingFn = compositingFn.replace(match[0], replacement)
        }
        compositingFn = `
                vec4 ${compositingFnName}(${["ivec2 texelIndex", "int lod", ...compositingFnArgs].join(", ")}) {
                    ${compositingFn}
                }
                `
        return {
            compositingFn,
            compositingFnImageOpArgs,
        }
    }

    private getSourceChannel(
        sourcePixelTransformHal: AnyImageOpPerPixelFnHalData,
        sourceIndex: number,
        channelIndex: number,
        compositingFnArgs: string[],
        compositingFnImageOpArgs: {
            imageOpHalData: AnyImageOpHalData
            channelIndex: number
            valueName: string
        }[],
    ) {
        if (Number.isNaN(sourceIndex) || sourceIndex < 0 || sourceIndex >= sourcePixelTransformHal.sourceValues.length) {
            throw new Error("ImageOpFnPixelTransform: Invalid source index: " + sourceIndex)
        }
        const source = sourcePixelTransformHal.sourceValues[sourceIndex]
        if (source instanceof ImageOpHalData) {
            if (channelIndex >= source.descriptor.channels) {
                if (source.descriptor.channels === 1) {
                    channelIndex = 0 // broadcast single channel
                } else {
                    return "0.0" // channel of multichannel source is out of range
                }
            }
            // does this arg exist already ?
            const foundArg = compositingFnImageOpArgs.find((desc) => desc.imageOpHalData === source && desc.channelIndex === channelIndex)
            if (foundArg) {
                return foundArg.valueName
            } else {
                const valueName = `value${compositingFnArgs.length}`
                compositingFnArgs.push(`float ${valueName}`)
                compositingFnImageOpArgs.push({
                    imageOpHalData: source,
                    channelIndex,
                    valueName,
                })
                return valueName
            }
        } else {
            // find parameter for this source
            const parameterName = this.sourceInfo.sourceParameterInfos.find(
                (info) => info.source === sourcePixelTransformHal && info.sourceIndex === sourceIndex,
            )?.parameterName
            if (!parameterName) {
                throw new Error("ImageOpFnPixelTransform: Parameter name not found")
            }
            return `${parameterName}${indexGlslVectorType(4, channelIndex)}`
        }
    }

    private getCurrentSourceParameters(): HalPainterParametersType {
        const sourceParameters = this.sourceInfo.sourceParameterInfos.map((info): HalPainterParametersType => {
            const sourceValue = info.source.sourceValues[info.sourceIndex]
            if (sourceValue instanceof ImageOpHalData) {
                return {}
            } else if (sourceValue instanceof ImageOpImgProc) {
                return {}
            } else {
                const color = colorFromRemotelyColorLike(sourceValue)
                return {
                    [info.parameterName]: {
                        type: "float4",
                        value: {x: color.r, y: color.g, z: color.b, w: color.a ?? 1},
                    },
                }
            }
        })
        return sourceParameters.reduce((acc, val) => ({...acc, ...val}), {})
    }

    // private get isCompiled() {
    //     return !!this._isForwardingTo
    // }

    // get isForwardingTo(): AnyImageOpPerPixelFnHalData | null {
    //     return this._isForwardingTo
    // }
    //
    // private get isForwardingToEventually(): AnyImageOpPerPixelFnHalData | null {
    //     let current: AnyImageOpPerPixelFnHalData | null = this._isForwardingTo
    //     while (current) {
    //         if (current._isForwardingTo) {
    //             current = current._isForwardingTo
    //         } else {
    //             return current
    //         }
    //     }
    //     return null
    // }

    readonly compositingInfo: HalCompositorInfo
    readonly sourceValues: EvaluatedImageOpValue<MapToDataType<SourceType>>[] = []

    private _sourceInfo: SourceInfo | null = null
    private _compositor: HalPainterImageCompositorRef | null = null
}

type SourceParameterInfo = {
    parameterName: string
    source: AnyImageOpPerPixelFnHalData
    sourceIndex: number
}

type SourceInfo = {
    sourceImages: AnyImageOpHalData[] // all source images (including those of merged pixel transforms)
    forwardedPerPixelFns: AnyImageOpPerPixelFnHalData[] // merged pixel transforms
    sourceParameterInfos: SourceParameterInfo[] // all source parameters (including those of merged pixel transforms)
}

export type HalCompositorInfo = {
    compositingFunction: string
    sourceParameterNames: string[] // TODO restrict this to actual parameter keys
    outDescriptor?: Partial<Descriptor> // default: automatically determined by sources
}
